import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Geofence, GeofenceRule } from '../models/organization.interface';
import { ConfigurationManagerService } from './configuration-manager.service';
import { ApiService } from './api.service';
import { GlobeService } from './globe.service';
import * as Cesium from 'cesium';

@Injectable({
  providedIn: 'root',
})
export class GeofenceService {
  private apiUrl = ConfigurationManagerService.settings.BaseURL;
  private integrationPulseUrl = ConfigurationManagerService.settings.integrationPulse;
  private geofencesSubject = new BehaviorSubject<Geofence[]>([]);
  private rulesSubject = new BehaviorSubject<GeofenceRule[]>([]);
  private geofences: Map<string, any> = new Map();
  private viewer: any;

  geofences$ = this.geofencesSubject.asObservable();
  rules$ = this.rulesSubject.asObservable();

  constructor(private api: ApiService, private globeService: GlobeService) {
    this.viewer = this.globeService.getViewer();
  }

  // Geofence CRUD operations
  getGeofences(): Observable<Geofence[]> {
    return this.api
      .post<{
        rows: any;
      }>(`${this.apiUrl + ConfigurationManagerService.settings.PerimeterFindCountAll}`, {
        where: { type: 'GEOFENCE' },
        limit: 10000,
      })
      .pipe(
        map((response) => {
          const geofences = response.rows;
          this.geofencesSubject.next(geofences);
          return geofences;
        })
      );
  }

  getCampuses(): Observable<Array<{
    id: string;
    label: string;
    type: string;
    geometry?: { type?: string; coordinates?: unknown };
  }>> {
    return this.api
      .post<{ rows: any[] }>(`${this.apiUrl + ConfigurationManagerService.settings.PerimeterFindCountAll}`, {
        where: { type: 'CAMPUS' },
        limit: 10000,
      })
      .pipe(
        map((response) => Array.isArray(response?.rows) ? response.rows : [])
      );
  }

  createGeofence(geofence: Partial<Geofence>): Observable<Geofence> {
    return this.api
      .post<Geofence>(`${this.apiUrl}db/Perimeter/createPerimeter`, {
          type: 'GEOFENCE',
          label: geofence.label,
          geometry: geofence.geometry,
          organizationIds: geofence.organizationIds,
       
      })
      .pipe(
        map((newGeofence) => {
          const currentGeofences = this.geofencesSubject.value;
          this.geofencesSubject.next([...currentGeofences, newGeofence]);
          return newGeofence;
        })
      );
  }

  deleteGeofence(id: string): Observable<void> {
    return this.api.delete<void>(`${this.apiUrl}db/Perimeter/${id}`).pipe(
      map(() => {
        const currentGeofences = this.geofencesSubject.value;
        this.geofencesSubject.next(currentGeofences.filter((g) => g.id !== id));
      })
    );
  }

  // Geofence Rules operations
  getPerimeterRules(): Observable<GeofenceRule[]> {
    return this.api
      .post<GeofenceRule[]>(`${this.integrationPulseUrl}/rule/find`, {
          where: { ruleType: 'PERIMETER_ENTER_EXIT' },
      })
      .pipe(
        map((response) => {
          this.rulesSubject.next(response);
          return response;
        })
      );
  }

  createRule(rule: Partial<GeofenceRule>): Observable<GeofenceRule> {
    return this.api
      .post<GeofenceRule>(`${this.integrationPulseUrl}/rule/create`, {
        body: rule,
      })
      .pipe(
        map((newRule) => {
          const currentRules = this.rulesSubject.value;
          this.rulesSubject.next([...currentRules, newRule]);
          return newRule;
        })
      );
  }

  updateRule(
    id: string,
    rule: Partial<GeofenceRule>
  ): Observable<GeofenceRule> {
    return this.api
      .put<GeofenceRule>(`${this.integrationPulseUrl}db/rule/${id}`, {
        body: rule,
      })
      .pipe(
        map((updatedRule) => {
          const currentRules = this.rulesSubject.value;
          this.rulesSubject.next(
            currentRules.map((r) => (r.id === id ? updatedRule : r))
          );
          return updatedRule;
        })
      );
  }

  deleteRule(id: string): Observable<void> {
    return this.api.delete<void>(`${this.integrationPulseUrl}db/rule/${id}`).pipe(
      map(() => {
        const currentRules = this.rulesSubject.value;
        this.rulesSubject.next(currentRules.filter((r) => r.id !== id));
      })
    );
  }

  createGeofenceForBuilding(buildingId: string, coordinates: number[][], radius: number = 50): void {
    if (!this.viewer) return;

    // Create a polygon for the geofence
    const positions = coordinates.map(coord => 
      Cesium.Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0)
    );

    const geofence = this.viewer.entities.add({
      name: `Geofence_${buildingId}`,
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(positions),
        material: Cesium.Color.RED.withAlpha(0.3),
        outline: true,
        outlineColor: Cesium.Color.RED,
        outlineWidth: 2,
        height: 0,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
      }
    });

    this.geofences.set(buildingId, geofence);

    // Add event handler for entity movement
    this.viewer.scene.postRender.addEventListener(() => {
      this.checkEntityInGeofence(buildingId, positions, radius);
    });
  }

  private checkEntityInGeofence(buildingId: string, positions: Cesium.Cartesian3[], radius: number): void {
    const entities = this.viewer.entities.values;
    const geofence = this.geofences.get(buildingId);

    entities.forEach((entity: any) => {
      if (entity.position && entity !== geofence) {
        const entityPosition = entity.position.getValue(Cesium.JulianDate.now());
        if (entityPosition) {
          const isInside = this.isPointInPolygon(entityPosition, positions);
          if (isInside) {
            // Entity is inside geofence
            this.onEntityEnterGeofence(buildingId, entity);
          } else {
            // Entity is outside geofence
            this.onEntityExitGeofence(buildingId, entity);
          }
        }
      }
    });
  }

  private isPointInPolygon(point: Cesium.Cartesian3, polygon: Cesium.Cartesian3[]): boolean {
    // Convert 3D points to 2D for polygon containment test
    const positions2D = polygon.map(pos => {
      const cartographic = Cesium.Cartographic.fromCartesian(pos);
      return [Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude)];
    });

    const point2D = [
      Cesium.Math.toDegrees(Cesium.Cartographic.fromCartesian(point).longitude),
      Cesium.Math.toDegrees(Cesium.Cartographic.fromCartesian(point).latitude)
    ];

    return this.pointInPolygon(point2D, positions2D);
  }

  private pointInPolygon(point: number[], polygon: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
      
      const intersect = ((yi > point[1]) !== (yj > point[1]))
          && (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  private onEntityEnterGeofence(buildingId: string, entity: any): void {
    // Handle entity entering geofence
    // You can emit an event or call a callback here
  }

  private onEntityExitGeofence(buildingId: string, entity: any): void {
    // Handle entity exiting geofence
    // You can emit an event or call a callback here
  }

  removeGeofence(buildingId: string): void {
    const geofence = this.geofences.get(buildingId);
    if (geofence) {
      this.viewer.entities.remove(geofence);
      this.geofences.delete(buildingId);
    }
  }

  updateGeofence(buildingId: string, coordinates: number[][], radius: number = 50): void {
    this.removeGeofence(buildingId);
    this.createGeofenceForBuilding(buildingId, coordinates, radius);
  }

}
